import { Link } from "react-router-dom"
import _ from "lodash"
import {
  Accordion,
  AccordionDetails,
  AccordionProps,
  AccordionSummary,
  Avatar,
  Box,
  CardMedia,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  SxProps,
  Typography,
  useTheme,
} from "@mui/material"
import { deepmerge } from "@mui/utils"
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded"
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded"

import { getIcon } from "utils/icon"
import { useTranslation } from "providers/i18n"
import { ServiceIcon } from "../../icons"

import { useVenue } from "providers/venue"
import {
  getFeatureDetail,
  getFeatureIDByPath,
  IAmenityDetail,
} from "utils/feature"
import { IServiceConfig } from "providers/venue/types"
import { isValidURL } from "utils/url"

export interface IServiceCardProps extends Omit<AccordionProps, "children"> {
  data: IServiceConfig
  sx?: SxProps
}

export const ServiceCard: React.FC<IServiceCardProps> = ({
  data,
  sx = {},
  ...props
}) => {
  const { t } = useTranslation()
  const { findFeatureById, deviceType } = useVenue()
  const { locations = [], name, description } = data
  const hasLocations = _.size(locations) > 0
  const theme = useTheme()
  const {
    root,
    accordionSummary,
    summaryContainer,
    titleText,
    descriptionText,
    serviceIconContainer,
    serviceIcon,
    infoContainer,
    accordionDetails,
    locationList,
    locationListItem,
    locationNavigateButton,
    floorTextAvatar,
    venueLogo: venueLogoStyle,
    venueLogoWrapper,
    divider,
    listNameText,
    serviceLabel,
  } = theme.components[`ServiceCard`]?.styleOverrides || {}

  return (
    <Accordion sx={deepmerge(root, sx)} {...props}>
      <AccordionSummary
        expandIcon={hasLocations && <ExpandMoreRoundedIcon />}
        sx={accordionSummary}
      >
        <Box sx={summaryContainer}>
          <Avatar sx={serviceIconContainer}>
            {getIcon(data, serviceIcon) || <ServiceIcon sx={serviceIcon} />}
          </Avatar>
          <Box sx={infoContainer}>
            <Typography variant="h4" sx={titleText}>
              {t(name)}
            </Typography>
            <Typography variant="body1" sx={descriptionText}>
              {t(description)}
            </Typography>
          </Box>
        </Box>
      </AccordionSummary>
      {hasLocations && (
        <AccordionDetails sx={accordionDetails}>
          <List disablePadding sx={locationList}>
            {locations.map((location, index) => {
              const path = location?.url
              const featureId = getFeatureIDByPath(location?.url)
              const isReferenceID = location.type === "reference-id"
              const feature = isReferenceID ? findFeatureById(featureId) : null
              const { venueLogo = "", level = "" } = feature
                ? (getFeatureDetail(feature) as IAmenityDetail) || {}
                : {}

              /**
               * Location handling rules:
               * - External URLs: render an <a> tag via the `component` prop in LinkItem.
               * - Internal paths: use `Link` from react-router-dom.
               * - Internal links can open in a new tab only when `is_new_tab=true`; otherwise, no target is set.
               * - External links support both `target="_blank"` and `target="_self"` (page reloads on navigation).
               * - //! NOTE: kiosk cannot open external links.
               */
              const isExternalLink =
                location.type === "custom" && isValidURL(location?.url)
              const isNewTab = location?.is_new_tab
              const renderLinkComponent = isExternalLink ? "a" : Link
              const renderLinkProps = isExternalLink
                ? { href: path, target: isNewTab ? "_blank" : "_self" }
                : { to: path, ...(isNewTab ? { target: "_blank" } : {}) }
              const disabled = isExternalLink && deviceType !== "mobile"

              return (
                <ListItem
                  key={`location-item-${index}`}
                  component={renderLinkComponent}
                  sx={locationListItem}
                  {...(!disabled ? renderLinkProps : {})}
                  secondaryAction={
                    !disabled &&
                    path && (
                      <IconButton
                        edge="end"
                        aria-label="navigate"
                        sx={locationNavigateButton}
                      >
                        <ChevronRightRoundedIcon />
                      </IconButton>
                    )
                  }
                >
                  {isReferenceID && (
                    <>
                      <Box sx={venueLogoWrapper}>
                        <CardMedia
                          src={venueLogo}
                          component="img"
                          sx={venueLogoStyle}
                        />
                      </Box>
                      <Divider orientation="vertical" sx={divider} />
                      <Typography sx={serviceLabel}>
                        {t(location?.label)}
                      </Typography>
                      <ListItemAvatar sx={floorTextAvatar}>
                        {t(level)}
                      </ListItemAvatar>
                    </>
                  )}
                  {!isReferenceID && (
                    <>
                      {location.image && (
                        <>
                          <Box
                            sx={venueLogoWrapper}
                            className="ServiceCard-venueLogoWrapper"
                          >
                            <CardMedia
                              src={location.image}
                              component="img"
                              sx={venueLogoStyle}
                            />
                          </Box>
                          <Divider orientation="vertical" sx={divider} />
                        </>
                      )}
                      <Typography sx={listNameText}>
                        {t(location?.label)}
                      </Typography>
                    </>
                  )}
                </ListItem>
              )
            })}
          </List>
        </AccordionDetails>
      )}
    </Accordion>
  )
}
